/**
 * The markdown the model actually emits, turned into the blocks v1.5 has components for.
 *
 * `14DR-0` (*Rich answer*) is the whole argument: "a heading, a table and numbered steps. prose was
 * only ever paragraphs." Three designed components — `AnswerHeading`, `DataTable`, `OrderedList` —
 * sat unused while the thread poured every reply into `AssistantProse`, so a table arrived as a wall
 * of pipe characters and a heading as a line beginning with a hash.
 *
 * **What this file is not.** It is not a markdown implementation. It recognises the six block shapes
 * the planning agent writes and four inline ones, and it deliberately stops there: the rule for
 * everything else is that it *stays as prose* rather than being approximated with a component that
 * means something different. A markdown blockquote is not a `SupersededQuote` (that block means "a
 * decision the thread overrode", and dimming an ordinary quotation to 55% would say so falsely), and
 * a bulleted list is not an `OrderedList` (its marker is the position, so bullets would come back
 * numbered). Both therefore stay prose. The two substitutions this file does make are named at their
 * call sites below.
 *
 * Pure data out. No React, for the same reason `turnModel.ts` holds no React: the parse is worth
 * testing on its own and the renderer is worth changing without touching it.
 */

/** A run inside a paragraph. `text` is already unescaped — the marks are gone by the time you see it. */
export type InlineRun =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'italic'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'link'; text: string; href: string };

/** One block of a structured answer. */
export type ProseNode =
  /** `#`…`######`. Flattened to one level — `AnswerHeading` is an `h3` and takes plain text only. */
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; runs: InlineRun[] }
  /** `1.` / `1)` — the only list with a component. */
  | { kind: 'steps'; items: InlineRun[][] }
  /** `-` / `*` / `+`. No component; rendered as one paragraph, one line per item. */
  | { kind: 'bullets'; items: InlineRun[][] }
  /** A GitHub pipe table. */
  | { kind: 'table'; columns: string[]; rows: string[][] }
  /** A ``` fence. No component; rendered as a paragraph in the mono face. */
  | { kind: 'code'; text: string };

/* ── inline ─────────────────────────────────────────────────────────────────────────────────── */

/**
 * `**bold**`, `*italic*` / `_italic_`, `` `code` ``, `[text](url)`.
 *
 * Earliest match wins, which is what keeps `**a** and *b*` from being read as one italic run: the
 * bold pattern matches at 0 and consumes both asterisk pairs before the italic pattern is consulted.
 * The italic pattern's own lookaround is the second half of that guarantee, for the case where the
 * bold run is not first.
 *
 * A code span is opaque: its text is pushed whole and never re-scanned, so backticks are the one
 * place markdown characters survive as themselves.
 */
export function parseInline(source: string): InlineRun[] {
  const runs: InlineRun[] = [];
  let rest = source;

  while (rest.length > 0) {
    const candidates: { at: number; length: number; run: InlineRun }[] = [];

    const code = /`([^`]+)`/.exec(rest);
    if (code) candidates.push({ at: code.index, length: code[0].length, run: { kind: 'code', text: code[1] } });

    const link = /\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/.exec(rest);
    if (link) {
      candidates.push({
        at: link.index,
        length: link[0].length,
        run: { kind: 'link', text: link[1] || link[2], href: link[2] },
      });
    }

    const bold = /\*\*([^*]+)\*\*|__([^_]+)__/.exec(rest);
    if (bold) {
      candidates.push({ at: bold.index, length: bold[0].length, run: { kind: 'bold', text: bold[1] ?? bold[2] } });
    }

    // The lookarounds are what stop this from eating one asterisk out of a `**bold**` pair, and what
    // keep `snake_case_words` from being read as an emphasis run — the underscore form only counts
    // when it is not glued to a word character on either side.
    const italic = /(?<!\*)\*([^*\n]+)\*(?!\*)|(?<![\w_])_([^_\n]+)_(?![\w_])/.exec(rest);
    if (italic) {
      candidates.push({
        at: italic.index,
        length: italic[0].length,
        run: { kind: 'italic', text: italic[1] ?? italic[2] },
      });
    }

    if (candidates.length === 0) {
      runs.push({ kind: 'text', text: rest });
      break;
    }

    // Ties go to whichever was pushed first, which is the order above: code, link, bold, italic.
    const next = candidates.reduce((best, one) => (one.at < best.at ? one : best));
    if (next.at > 0) runs.push({ kind: 'text', text: rest.slice(0, next.at) });
    runs.push(next.run);
    rest = rest.slice(next.at + next.length);
  }

  return runs.filter((run) => run.kind !== 'text' || run.text.length > 0);
}

/* ── blocks ─────────────────────────────────────────────────────────────────────────────────── */

const HEADING = /^\s{0,3}(#{1,6})\s+(.*)$/;
const ORDERED = /^\s*(\d{1,3})[.)]\s+(.*)$/;
const BULLET = /^\s*[-*+]\s+(.*)$/;
const FENCE = /^\s*```/;
/** `---`, `***`, `___` on their own line. Dropped: there is no rule component in eighty artboards. */
const THEMATIC = /^\s{0,3}([-*_])\s*(?:\1\s*){2,}$/;
const BLOCKQUOTE = /^\s{0,3}>\s?(.*)$/;

/** A table row is a line with a pipe at each end once trimmed, or at least one interior pipe. */
function looksLikeTableRow(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.includes('|', 1);
}

/** `|---|:--:|---:|` — the second line of every GitHub table and the only thing that proves one. */
function isTableDelimiter(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.startsWith('|')) return false;
  return splitTableRow(trimmed).every((cell) => /^:?-{1,}:?$/.test(cell.trim()) && cell.trim().length > 0);
}

/** `| a | b |` → `['a', 'b']`. Escaped pipes (`\|`) stay inside their cell. */
function splitTableRow(line: string): string[] {
  const body = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  const cells: string[] = [];
  let cell = '';
  for (let i = 0; i < body.length; i += 1) {
    if (body[i] === '\\' && body[i + 1] === '|') {
      cell += '|';
      i += 1;
      continue;
    }
    if (body[i] === '|') {
      cells.push(cell);
      cell = '';
      continue;
    }
    cell += body[i];
  }
  cells.push(cell);
  return cells.map((one) => one.trim());
}

/**
 * `**bold**` etc. out of a string that is going somewhere plain.
 *
 * `AnswerHeadingProps.children` is typed `string` on purpose — "a heading with markup in it is a
 * paragraph pretending to be a heading" — and `DataTable` takes `string[][]`. Both get the marks
 * stripped rather than rendered, which is the same call `rehydrate.ts` already makes for the
 * thinking recap.
 */
function plain(source: string): string {
  return parseInline(source)
    .map((run) => run.text)
    .join('')
    .trim();
}

/**
 * One assistant answer → the blocks it should draw.
 *
 * A single forward pass over the lines. Blank lines close whatever is open; everything else either
 * continues the open block or starts a new one, and a block that no rule claims lands in a paragraph.
 */
export function parseAnswer(source: string): ProseNode[] {
  const nodes: ProseNode[] = [];
  const lines = source.replace(/\r\n?/g, '\n').split('\n');

  /** The paragraph being accumulated, as raw lines. Soft-wrapped input joins with a space. */
  let paragraph: string[] = [];
  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const text = paragraph.join(' ').trim();
    paragraph = [];
    if (text) nodes.push({ kind: 'paragraph', runs: parseInline(text) });
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      flushParagraph();
      i += 1;
      continue;
    }

    if (THEMATIC.test(line)) {
      flushParagraph();
      i += 1;
      continue;
    }

    if (FENCE.test(line)) {
      flushParagraph();
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !FENCE.test(lines[i])) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1; // the closing fence, or the end of the input
      const text = body.join('\n').replace(/\n+$/, '');
      if (text) nodes.push({ kind: 'code', text });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flushParagraph();
      const text = plain(heading[2]);
      if (text) nodes.push({ kind: 'heading', text });
      i += 1;
      continue;
    }

    // A table needs its delimiter row to be a table at all; a lone `| something |` is a sentence
    // with pipes in it and stays prose.
    if (looksLikeTableRow(line) && i + 1 < lines.length && isTableDelimiter(lines[i + 1])) {
      flushParagraph();
      const columns = splitTableRow(line).map(plain);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && looksLikeTableRow(lines[i])) {
        const cells = splitTableRow(lines[i]).map(plain);
        // Short rows are padded rather than left ragged — `DataTable` renders by column index.
        rows.push(columns.map((_column, at) => cells[at] ?? ''));
        i += 1;
      }
      nodes.push({ kind: 'table', columns, rows });
      continue;
    }

    if (ORDERED.test(line) || BULLET.test(line)) {
      flushParagraph();
      const ordered = ORDERED.test(line);
      const items: InlineRun[][] = [];
      // Continuation lines (an indented run under an item, no marker of its own) join the item
      // above rather than starting a paragraph in the middle of the list.
      while (i < lines.length) {
        const current = lines[i];
        const match = ordered ? ORDERED.exec(current) : BULLET.exec(current);
        if (match) {
          items.push(parseInline(ordered ? match[2] : match[1]));
          i += 1;
          continue;
        }
        if (items.length > 0 && /^\s+\S/.test(current) && !ORDERED.test(current) && !BULLET.test(current)) {
          items[items.length - 1] = parseInline(
            items[items.length - 1].map((run) => run.text).join('') + ' ' + current.trim(),
          );
          i += 1;
          continue;
        }
        break;
      }
      nodes.push(ordered ? { kind: 'steps', items } : { kind: 'bullets', items });
      continue;
    }

    // A blockquote keeps its words and loses its rule. `SupersededQuote` is the only quote block in
    // the design and it means "an earlier call the later one overrode", which an ordinary quotation
    // is not — see the file header.
    const quote = BLOCKQUOTE.exec(line);
    if (quote) {
      paragraph.push(quote[1]);
      i += 1;
      continue;
    }

    paragraph.push(line);
    i += 1;
  }

  flushParagraph();
  return nodes;
}

/* ── the table's columns ────────────────────────────────────────────────────────────────────── */

/** Roughly one character of the 13px table face. Only ever used to size a column, never to lay out. */
const CELL_CHAR = 8;
const CELL_PADDING = 20;
const MIN_COLUMN = 56;
const MAX_COLUMN = 160;

/**
 * `DataTable` wants a width per column and markdown carries none.
 *
 * The design's own table is `70 / 60 / 'fill'` — two narrow columns of numbers and one that absorbs
 * the rest — so the rule here reproduces that shape rather than splitting evenly: the last column
 * always fills, and every other column is sized to its longest cell. An even split is legal (the
 * component says so) and looks wrong the moment one column holds a sentence and the next holds `5`.
 */
export function tableColumns(columns: string[], rows: string[][]): { header: string; width: number | 'fill' }[] {
  return columns.map((header, at) => {
    if (at === columns.length - 1) return { header, width: 'fill' as const };
    const longest = rows.reduce((max, row) => Math.max(max, (row[at] ?? '').length), header.length);
    return { header, width: Math.min(MAX_COLUMN, Math.max(MIN_COLUMN, longest * CELL_CHAR + CELL_PADDING)) };
  });
}

/* ── the user bubble's clamp ────────────────────────────────────────────────────────────────── */

/**
 * How many characters of the 320px bubble fit on one line, and how many lines before it clamps.
 *
 * Both numbers are read off `14DR-0`'s own table rather than guessed: it states 200 chars → 5 lines
 * → 127px, 1000 → 23 → 505px, 3000 → 69 → 1471px. 21px leading plus the bubble's 11px of block
 * padding either side reproduces all three heights exactly (5 × 21 + 22 = 127), and the three
 * char-per-line ratios agree at ~43. The clamp is 168px, which is 8 × 21 — and the same artboard's
 * step 1 says it in words: "clamp at 8 lines behind a fade".
 */
const BUBBLE_CHARS_PER_LINE = 43;
const BUBBLE_CLAMP_LINES = 8;

/**
 * Is this message long enough to earn the clamp and its footer?
 *
 * Estimated rather than measured, and the trade is deliberate. Measuring means rendering the bubble
 * unclamped, reading its height and clamping on the next frame — a visible jump on every long
 * message in a reloaded thread, on the one component whose artboard is specifically about not
 * moving. The estimate is wrong only within a line or two of the boundary, where the fade shows
 * nothing being hidden and the footer is merely redundant.
 */
export function isLongMessage(text: string): boolean {
  let lines = 0;
  for (const line of text.split('\n')) {
    lines += Math.max(1, Math.ceil(line.length / BUBBLE_CHARS_PER_LINE));
    if (lines > BUBBLE_CLAMP_LINES) return true;
  }
  return false;
}
